import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Rating,
  Text,
  P,
  ExpandableContent,
  Level,
  Media,
  Clipped,
  List,
  Button,
  Icon,
} from '@deseretbook/react-ui'
import { useFeedback } from './useFeedback'
import { formatDate } from './formatDate'

/**
 * Highlighted text component. Renders text but with any section matching
 * the specified query highlighted.
 * @param {string} props.text - Text to display
 * @param {string} props.query - Query to highlight in text.
 * @returns {function} Component
 */
const HighlightedText = ({ text, query }) => {
  if (!query || !text) {
    return text || false
  }
  /**
   * Splits query by space for multiple keyword highlighting.
   * The string is joined with a '|' or "OR" statement in regex.
   */
  const multiQuery = query
    .split(' ')
    .filter(Boolean)
    .join('|')

  return text
    .replace(new RegExp(multiQuery, 'gi'), match => `|${match}|`)
    .split('|')
    .filter(Boolean)
    .map(segment => {
      if (new RegExp(multiQuery, 'gi').test(segment)) {
        return (
          <mark style={{ background: 'rgba(255,255,100,0.5)' }}>{segment}</mark>
        )
      }

      return segment
    })
}

/**
 * This returns a Review Component
 * @param {obj} review - The review object
 * @param {string} keywords - Keywords used for highlighting text within reviews
 * @returns {func} Component
 */
export const Review = ({
  rating,
  name,
  createdAt,
  review,
  title,
  verifiedPurchaser,
  keywords,
  id,
  feedbackReviews,
  allowFeedback = true,
}) => {
  const { provideFeedback, userFeedback, positiveFeedback } = useFeedback(
    feedbackReviews,
    id,
  )

  const reviewDate = formatDate(createdAt, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

  return (
    <Media>
      <Media.Content>
        <Level>
          <Level.Left>
            <Level.Item>
              <Rating average={rating} />
            </Level.Item>
          </Level.Left>
          <Level.Item>
            <Clipped>
              <Text hasEllipsis isBold title={title}>
                <HighlightedText text={title} query={keywords} />
              </Text>
            </Clipped>
          </Level.Item>
        </Level>
        <Box paddingBottom={0.5}>
          <List type="horizontal">
            <List.Item>
              <Text isItalic>{name || 'Anonymous'}</Text>
            </List.Item>
            <List.Item>
              <Text>{reviewDate}</Text>
            </List.Item>
            {verifiedPurchaser && (
              <List.Item>
                <Text isBold color="primary">
                  Verified Purchaser
                </Text>
              </List.Item>
            )}
          </List>
        </Box>
        <ExpandableContent collapsedHeight={6}>
          <P>
            <HighlightedText text={review} query={keywords} />
          </P>
        </ExpandableContent>

        {allowFeedback && positiveFeedback >= 1 && (
          <Box alignItems="center" marginBottom={1}>
            <Level>
              <Level.Left>
                <Level.Item>
                  <Icon name="thumbUp" />
                </Level.Item>
              </Level.Left>
              <Level.Item>
                <Text color="secondary" shade="light">
                  {positiveFeedback}{' '}
                  {positiveFeedback === 1 ? 'customer' : 'customers'} found this
                  helpful.
                </Text>
              </Level.Item>
            </Level>
          </Box>
        )}
        {allowFeedback && window.__USER__.loggedIn && (
          <Box justifyContent="flex-end" alignItems="center" isFullwidth>
            <Box isInline justifyContent="start" marginRight={1.5}>
              <Button
                icon="thumbUp"
                color={
                  userFeedback && userFeedback.rating === 5
                    ? 'secondary'
                    : 'default'
                }
                size="small"
                onClick={() => provideFeedback(5)}
              >
                Helpful
              </Button>
            </Box>

            <Box isInline justifyContent="start">
              <Button
                icon="thumbDown"
                color={
                  userFeedback && userFeedback.rating === 1
                    ? 'secondary'
                    : 'default'
                }
                size="small"
                onClick={() => provideFeedback(1)}
              >
                Not Helpful
              </Button>
            </Box>
          </Box>
        )}
      </Media.Content>
    </Media>
  )
}

Review.propTypes = {
  rating: PropTypes.number,
  name: PropTypes.string,
  createdAt: PropTypes.string,
  review: PropTypes.string,
  title: PropTypes.string,
  verifiedPurchaser: PropTypes.bool,
  keywords: PropTypes.string,
  id: PropTypes.number,
  feedbackReviews: PropTypes.arrayOf(PropTypes.object),
  allowFeedback: PropTypes.bool,
}
